import React, { useEffect, useMemo, useState } from 'react';

import MessageInfo from '../../../../components/messageInfo/MessageInfo';
import NonPayoutMetricsPanel from '../../../../components/nonPayoutMetricsPanel/NonPayoutMetricsPanel';
import StandardMetricsPanel from '../../../../components/standardMetricsPanel/StandardMetricsPanel';
import {
    useGetDailyMetricsSummaryQuery,
    useGetMonthlyMetricHistoryQuery,
} from '../../../../pages/metricdashboard/metricDashboardSlice';
import { useGetTeamLeadMonthlyMetricsSummaryQuery } from '../../../../pages/scorecardDashboard/scorecardDashboardSlice';
import GradientDefs, { safeMetricColorIndex } from '../../../../components/metricMonthlySummaryCard/GradientDefs';
import standardMetricsIcon from '../../../../assets/standardMetrics.png';
import additionalMetricsIcon from '../../../../assets/additionalMetrics.png';

interface PropTypes {
    empNum?: number;
    inputyear?: number;
    inputmonth?: number;
    scorecardVersion?: number;
}

const CURRENT_MONTH_LABEL = 'Last 30 Days';
const HISTORY_LABEL = 'Last 6 Months';

const CoachingActionPlanScoreCardDashboard = ({
    empNum,
    inputyear,
    inputmonth,
    scorecardVersion,
}: PropTypes) => {
    const [activeTab, setActiveTab] = useState<'standard' | 'additional'>('standard');
    const [selectedMetricName, setSelectedMetricName] = useState<string>('');
    const [selectedMetricColorIndex, setSelectedMetricColorIndex] = useState(0);
 
    const shouldSkip = !empNum || !inputyear || !inputmonth || !scorecardVersion;
 
    const { data: monthlyMetricsSummary } = useGetTeamLeadMonthlyMetricsSummaryQuery(
        {
            employeenumber: empNum,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: 2,
            isFromCoachingAction: true,
        },
        { skip: shouldSkip }
    );
 
    const { data: monthlyMetricHistory } = useGetMonthlyMetricHistoryQuery(
        {
            employeenumber: empNum,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: 2,
        },
        { skip: shouldSkip }
    );
 
    const { data: dailyMetricsData } = useGetDailyMetricsSummaryQuery(
        {
            employeenumber: empNum,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: 2,
            isFromCoachingAction: true,
        },
        { skip: shouldSkip }
    );
 
    const payoutMetrics = useMemo(
        () => monthlyMetricsSummary?.data?.filter((m: any) => m.is_payout_metric === true) ?? [],
        [monthlyMetricsSummary]
    );
 
    const nonPayoutMetrics = useMemo(
        () => monthlyMetricsSummary?.data?.filter((m: any) => m.is_payout_metric === false) ?? [],
        [monthlyMetricsSummary]
    );
 
    useEffect(() => {
        if (payoutMetrics.length > 0) {
            setSelectedMetricName(payoutMetrics[0].label);
            setSelectedMetricColorIndex(0);
        } else {
            setSelectedMetricName('');
            setSelectedMetricColorIndex(0);
        }
    }, [payoutMetrics]);
 
    const handleCardClick = (metricName: string, index: number) => {
        setSelectedMetricName(metricName);
        setSelectedMetricColorIndex(safeMetricColorIndex(index));
    };
 
    const hasAnyMetrics = (monthlyMetricsSummary?.data?.length ?? 0) > 0;
    const hasAdditionalMetrics = nonPayoutMetrics.length > 0;
 
    if (!hasAnyMetrics) {
        return (
            <>
                <GradientDefs />
                <div className="col-12 col-md-12 ps-4 pb-4 mt-3">
                    <MessageInfo
                        title="No results found for Agent Monthly Metric."
                        message="Try adjusting the month or scorecard filter above."
                        rowHeight="208px"
                    />
                </div>
            </>
        );
    }
 
    return (
        <>
            <GradientDefs />
            <div className="card shadow bg-body rounded mt-3 border-1">
                <div className="cap-scorecard-tabs">
                    {hasAdditionalMetrics && (
                    <div className="metrics-tab-switcher">
                        <button
                            className={`metrics-tab ${activeTab === 'standard' ? 'metrics-tab--active' : ''}`}
                            onClick={() => setActiveTab('standard')}
                            type="button"
                        >
                            <img src={standardMetricsIcon} alt="Standard Metrics" style={{ width: '16px', height: '16px' }} />
                            Standard Metrics
                        </button>
                        <button
                            className={`metrics-tab ${activeTab === 'additional' ? 'metrics-tab--active' : ''} ${!hasAdditionalMetrics ? 'metrics-tab--disabled' : ''}`}
                            onClick={() => setActiveTab('additional')}
                            disabled={!hasAdditionalMetrics}
                            type="button"
                        >
                            <img src={additionalMetricsIcon} alt="Additional Metrics" style={{ width: '16px', height: '16px' }} />
                            Additional Metrics (Non Payout)
                        </button>
                    </div>
                    )}
 
                    <div className="metrics-tab-content">
                        {activeTab === 'standard' && (
                            <StandardMetricsPanel
                                metrics={payoutMetrics}
                                dailyMetricsData={dailyMetricsData}
                                monthlyMetricHistory={monthlyMetricHistory}
                                scorecardVersionId={scorecardVersion ?? 0}
                                employeeNumber={empNum?.toString() ?? ''}
                                fromMetricDashboard={false}
                                selectedMetricName={selectedMetricName}
                                selectedMetricColorIndex={selectedMetricColorIndex}
                                onCardClick={handleCardClick}
                                currentMonthLabel={CURRENT_MONTH_LABEL}
                                historyLabel={HISTORY_LABEL}
                            />
                        )}
                        {activeTab === 'additional' && (
                            <NonPayoutMetricsPanel
                                metrics={nonPayoutMetrics}
                                dailyMetricsData={dailyMetricsData}
                                monthlyMetricHistory={monthlyMetricHistory}
                                scorecardVersionId={scorecardVersion ?? 0}
                                employeeNumber={empNum?.toString() ?? ''}
                                currentMonthLabel={CURRENT_MONTH_LABEL}
                                historyLabel={HISTORY_LABEL}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
 
export default CoachingActionPlanScoreCardDashboard;